import React, { useState } from 'react';
import { IconButton, Tooltip, Box } from '@mui/material';
import { ThumbUp, ThumbDown } from '@mui/icons-material';
import { useFeedback } from '../contexts/FeedbackContext';
import { FeedbackState } from '../types/feedback';
import NegativeFeedbackFlyout from './NegativeFeedbackFlyout';

interface FeedbackButtonsProps {
  messageId: string;
  existingFeedback?: FeedbackState;
}

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({ 
  messageId, 
  existingFeedback 
}) => {
  const { 
    feedbackConfig, 
    submitFeedback, 
    updateFeedback,
    getFeedbackForMessage 
  } = useFeedback();
  
  const [showNegativeFlyout, setShowNegativeFlyout] = useState(false);
  const currentFeedback = existingFeedback || getFeedbackForMessage(messageId);

  // Don't render if feedback system is disabled
  if (!feedbackConfig?.enabled) {
    return null;
  }

  const handlePositiveFeedback = async () => {
    try {
      if (currentFeedback?.type === 'positive') {
        // If already positive and changes are allowed, we could remove feedback
        // For now, just ignore the click
        return;
      }

      if (currentFeedback && feedbackConfig.allowChanges) {
        await updateFeedback(messageId, 'positive');
      } else if (!currentFeedback) {
        await submitFeedback(messageId, 'positive');
      }
    } catch (error) {
      console.error('Failed to submit positive feedback:', error);
    }
  };

  const handleNegativeFeedback = async () => {
    try {
      if (currentFeedback?.type === 'negative') {
        // If already negative and changes are allowed, could show flyout to edit
        if (feedbackConfig.allowChanges && feedbackConfig.negativeFlyoutEnabled) {
          setShowNegativeFlyout(true);
        }
        return;
      }

      if (feedbackConfig.negativeFlyoutEnabled) {
        // Open flyout for detailed feedback
        setShowNegativeFlyout(true);
      } else {
        // Submit negative feedback immediately without flyout
        if (currentFeedback && feedbackConfig.allowChanges) {
          await updateFeedback(messageId, 'negative', { bypassFlyout: true });
        } else if (!currentFeedback) {
          await submitFeedback(messageId, 'negative', { bypassFlyout: true });
        }
      }
    } catch (error) {
      console.error('Failed to handle negative feedback:', error);
    }
  };

  const handleNegativeFlyoutSubmit = async (reasons: string[], comment?: string) => {
    try {
      if (currentFeedback && feedbackConfig.allowChanges) {
        await updateFeedback(messageId, 'negative', { reasons, comment });
      } else if (!currentFeedback) {
        await submitFeedback(messageId, 'negative', { reasons, comment });
      }
      setShowNegativeFlyout(false);
    } catch (error) {
      console.error('Failed to submit negative feedback:', error);
    }
  };

  const handleNegativeFlyoutClose = () => {
    setShowNegativeFlyout(false);
  };

  const getButtonColor = (type: 'positive' | 'negative'): string => {
    if (currentFeedback?.type === type) {
      return feedbackConfig.theme.selectedColor;
    }
    
    return type === 'positive' 
      ? feedbackConfig.theme.positiveColor 
      : feedbackConfig.theme.negativeColor;
  };

  const getButtonOpacity = (type: 'positive' | 'negative'): number => {
    if (currentFeedback?.type === type) {
      return 1.0;
    }
    return currentFeedback && currentFeedback.type !== type ? 0.3 : 0.7;
  };

  return (
    <>
      <Box 
        sx={{ 
          display: 'flex', 
          gap: 0.5, 
          alignItems: 'center',
          mt: 0.5
        }}
      >
        <Tooltip 
          title={feedbackConfig.text.positiveTooltip}
          placement="top"
        >
          <IconButton
            size="small"
            onClick={handlePositiveFeedback}            disabled={
              Boolean(currentFeedback?.type === 'positive') || 
              Boolean(currentFeedback && !feedbackConfig.allowChanges)
            }
            sx={{
              color: getButtonColor('positive'),
              opacity: getButtonOpacity('positive'),
              '&:hover': {
                backgroundColor: `${getButtonColor('positive')}20`,
                opacity: 1.0
              },
              '&:disabled': {
                opacity: 0.3
              }
            }}
          >
            <ThumbUp fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip 
          title={feedbackConfig.text.negativeTooltip}
          placement="top"
        >
          <IconButton
            size="small"
            onClick={handleNegativeFeedback}            disabled={
              Boolean(currentFeedback?.type === 'negative' && !feedbackConfig.allowChanges)
            }
            sx={{
              color: getButtonColor('negative'),
              opacity: getButtonOpacity('negative'),
              '&:hover': {
                backgroundColor: `${getButtonColor('negative')}20`,
                opacity: 1.0
              },
              '&:disabled': {
                opacity: 0.3
              }
            }}
          >
            <ThumbDown fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Negative Feedback Flyout */}
      {feedbackConfig.negativeFlyoutEnabled && (
        <NegativeFeedbackFlyout
          open={showNegativeFlyout}
          messageId={messageId}
          feedbackConfig={feedbackConfig}
          existingFeedback={currentFeedback}
          onSubmit={handleNegativeFlyoutSubmit}
          onClose={handleNegativeFlyoutClose}
        />
      )}
    </>
  );
};

export default FeedbackButtons;
